import { Email, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import { useAuth } from '../contexts/AuthContext';
import SpotifyService from '../services/spotifyService';

const Login = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { loginWithCredentials } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await loginWithCredentials(email, password);
      if (result.success) {
        // Rediriger vers le dashboard
        navigate('/dashboard');
      } else {
        setError(result.error || 'Une erreur est survenue lors de la connexion.');
      }
    } catch (err) {
      setError('Identifiants incorrects. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSpotifyLogin = () => {
    // Rediriger vers l'authentification Spotify
    SpotifyService.initiateLogin('web');
  };

  return (
    <PageTransition>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            py: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
                sx={{
                  borderRadius: '50px',
                  px: 3,
                  py: 1,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.dark,
                    backgroundColor: 'rgba(255, 107, 107, 0.05)',
                  },
                }}
              >
                Retour à l'accueil
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            py: { xs: 6, md: 10 },
          }}
        >
          <Container maxWidth="md">
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: 'text.primary',
                    }}
                  >
                    Connexion
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      mb: 4,
                    }}
                  >
                    Connectez-vous à votre compte Jukobox pour accéder à toutes vos fonctionnalités.
                  </Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <form onSubmit={handleSubmit}>
                    {error && (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                      </Alert>
                    )}

                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      onClick={handleSpotifyLogin}
                      sx={{
                        borderRadius: '50px',
                        py: 1.5,
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        '&:hover': {
                          borderColor: theme.palette.primary.dark,
                          backgroundColor: 'rgba(255, 107, 107, 0.05)',
                        },
                      }}
                    >
                      Se connecter avec Spotify
                    </Button>

                    <Divider sx={{ my: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        ou
                      </Typography>
                    </Divider>

                    <TextField
                      fullWidth
                      label="Email"
                      variant="outlined"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      sx={{ mb: 3 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Mot de passe"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      sx={{ mb: 4 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={isLoading}
                      onClick={handleSubmit}
                      sx={{
                        borderRadius: '50px',
                        py: 1.5,
                        mb: 3,
                        fontSize: '1.1rem',
                        boxShadow: '0 4px 14px 0 rgba(255, 107, 107, 0.2)',
                        '&:hover': {
                          boxShadow: '0 6px 20px 0 rgba(255, 107, 107, 0.3)',
                        },
                      }}
                    >
                      {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Se connecter'}
                    </Button>

                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Mot de passe oublié ?{' '}
                        <Button
                          variant="text"
                          color="primary"
                          onClick={() => navigate('/forgot-password')}
                          sx={{ p: 0, minWidth: 'auto', ml: 0.5, boxShadow: 'none' }}
                        >
                          Réinitialiser
                        </Button>
                      </Typography>
                    </Box>
                  </form>
                </Paper>

                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Vous n'avez pas de compte ?{' '}
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => navigate('/register')}
                      sx={{ p: 0, minWidth: 'auto', ml: 0.5, boxShadow: 'none' }}
                    >
                      S'inscrire
                    </Button>
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }}>
                <Box
                  sx={{
                    height: '100%',
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/img/login-illustration.png"
                    alt="Login Illustration"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: 500,
                      objectFit: 'contain',
                      filter: 'brightness(0.95) contrast(1.1)',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        'linear-gradient(45deg, rgba(255, 107, 107, 0.1), rgba(78, 205, 196, 0.1))',
                      zIndex: -1,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </PageTransition>
  );
};

export default Login;
